import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, User, Plus, Menu, LogOut, Coins, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/i18n";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const LangSwitch = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center rounded-full bg-[#F1F4F9] p-0.5" data-testid="lang-switch">
      <Globe className="ml-2 h-3.5 w-3.5 text-[#64748B]" />
      {Object.entries(LANGS).map(([k, v]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`lang-${k}-btn`}
          className={`rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${lang === k ? "bg-white text-[#3B1272] shadow-sm" : "text-[#64748B]"}`}>
          {v.label}
        </button>
      ))}
    </div>
  );
};

const navItems = (t) => [
  { to: "/cerca", label: t("nav.explore"), id: "explore" },
  { to: "/cerca?area=outlet", label: "Outlet", id: "outlet" },
  { to: "/cerca?exchange=true", label: t("areas.scambi.t").replace("Juppo ", ""), id: "scambi" },
  { to: "/come-funziona", label: t("nav.how"), id: "how" },
];

const UserMenu = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-[#F1F4F9]">
        <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#EFE9FB] text-sm font-bold text-[#3B1272]">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.first_name || user.name || "?")[0]}
        </span>
        <span className="hidden max-w-[110px] truncate text-sm font-semibold lg:block">{user.first_name || user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl bg-white">
        <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/account")}><User className="mr-2 h-4 w-4" />{t("nav.account")}</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-wallet" onClick={() => nav("/account?tab=wallet")}><Coins className="mr-2 h-4 w-4 text-amber-500" />Juppo Coin</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-favorites" onClick={() => nav("/account?tab=favorites")}><Heart className="mr-2 h-4 w-4" />{t("nav.favorites")}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />{t("nav.logout")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { user, favIds } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isCustomer = user && !["admin", "moderator", "operator"].includes(user.role);
  return (
    <header className="sticky top-0 z-40 border-b border-[#EEF0F6] bg-white/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Logo />
        <nav className="ml-6 hidden items-center gap-1 md:flex">
          {navItems(t).map((n) => (
            <NavLink key={n.id} to={n.to} data-testid={`nav-${n.id}`} className="rounded-full px-3.5 py-2 text-sm font-semibold text-[#1E1B2E] transition-colors hover:bg-[#EFE9FB] hover:text-[#3B1272]">{n.label}</NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangSwitch /></div>
          <Link to={isCustomer ? "/account?tab=favorites" : "/accedi"} data-testid="header-favorites-link" className="relative hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold hover:bg-[#F1F4F9] sm:flex">
            <Heart className="h-5 w-5" /><span className="hidden lg:inline">{t("nav.favorites")}</span>
            {favIds.size > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF7A6B] px-1 text-[10px] font-bold text-white" data-testid="favorites-count">{favIds.size}</span>}
          </Link>
          {isCustomer && <NotificationBell />}
          {user ? <UserMenu /> : (
            <Link to="/accedi" data-testid="header-login-link" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold hover:bg-[#F1F4F9]"><User className="h-5 w-5" /><span className="hidden sm:inline">{t("nav.login")}</span></Link>
          )}
          <Link to="/vendi" data-testid="header-sell-btn" className="btn-coral hidden !py-2 sm:inline-flex"><Plus className="h-4 w-4" />{t("nav.sell")}</Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="rounded-full p-2 hover:bg-[#F1F4F9] md:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white">
              <div className="mt-8 flex flex-col gap-1">
                {navItems(t).map((n) => (
                  <Link key={n.id} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}`} className="rounded-xl px-3 py-3 font-semibold hover:bg-[#EFE9FB]">{n.label}</Link>
                ))}
                {isCustomer && (
                  <>
                    <Link to="/account?tab=favorites" onClick={() => setOpen(false)} data-testid="mobile-nav-favorites" className="rounded-xl px-3 py-3 font-semibold hover:bg-[#EFE9FB]">{t("nav.favorites")}</Link>
                    <Link to="/account" onClick={() => setOpen(false)} data-testid="mobile-nav-account" className="rounded-xl px-3 py-3 font-semibold hover:bg-[#EFE9FB]">{t("nav.account")}</Link>
                  </>
                )}
                <Link to="/vendi" onClick={() => setOpen(false)} className="btn-coral mt-3" data-testid="mobile-sell-btn"><Plus className="h-4 w-4" />{t("nav.sell")}</Link>
                <div className="mt-4"><LangSwitch /></div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
